import services from '../services';
import { extractErrorMessage } from '../utils/helpers';
import { makeAutoObservable, runInAction } from 'mobx';
import {
    generateCreateEntity,
    generateDeleteEntity,
    generateLoadEntity,
    generateUpdateEntity,
} from '../utils/mobx';

class PartnerStore {
    error = null;
    editingCompany = null;
    partnerSubscriptionsOverview = null;
    collectingPayment = false;
    loadingPartnerCompanies = false;
    loadingCompany = false;
    loadingCompanies = false;
    savingCompany = false;
    savingPPV = false;
    savingPPVApiKey = false;
    addingManager = false;
    loadingPPVLog = false;
    loadingPPVTransactions = false;
    loadingPartnerSubscriptionsOverview = false;
    uploadingLogoAsPartner = false;
    companySearchValue = '';
    companies = [];
    currentPartnerId = null;
    settingsLoaded = false;
    uiSettings = null;
    savingPartnerName = false;
    loadingSettings = false;
    uploadingLogo = false;
    removingLogo = false;
    uploadingFavicon = false;
    removingFavicon = false;
    ppvLog = [];
    ppvTransactions = [];
    removingCompanies = [];

    constructor(authStore) {
        makeAutoObservable(this);
        this.authStore = authStore;
    }

    setError(error, name) {
        switch (name) {
            default:
                this.error = error;
        }
    }

    get currentPartner() {
        if (!this.currentPartnerId) return null;
        return (
            this.authStore.currentUser?.superForAccounts?.find(
                (p) => p.id === this.currentPartnerId
            ) || null
        );
    }

    get logoUrl() {
        return this.uiSettings?.['ui.logo'] || null;
    }

    get faviconUrl() {
        return this.uiSettings?.['ui.favicon'] || null;
    }

    setCompanySearchValue(companySearchValue) {
        this.companySearchValue = companySearchValue;
    }

    updatePartnerName = generateUpdateEntity(
        'updatePartnerName',
        this,
        'savingPartnerName',
        async (partnerId, name) => {
            await services.Partners.update(partnerId, { name });
            this.setPartnerName(partnerId, name);
            return name;
        },
        null,
        { throwError: true }
    );

    loadSettings = generateLoadEntity(
        'loadSettings',
        this,
        'loadingSettings',
        async (partnerId) => {
            const numId = Number(partnerId);
            if (this.currentPartnerId === numId && this.settingsLoaded) return;
            this.currentPartnerId = numId;
            const data = await services.Partners.getSettings(partnerId);
            this.uiSettings = data;
            this.settingsLoaded = true;
            return data;
        },
        () => {}
    );

    uploadLogo = generateCreateEntity(
        'uploadLogo',
        this,
        'uploadingLogo',
        async (partnerId, formData) => {
            const result = await services.Partners.uploadLogo(
                partnerId,
                formData
            );
            this.uiSettings = {
                ...this.uiSettings,
                'ui.logo': result.url || result,
            };
            return result;
        },
        null,
        { throwError: true }
    );

    removeLogo = generateDeleteEntity(
        'removeLogo',
        this,
        'removingLogo',
        async (partnerId) => {
            await services.Partners.removeLogo(partnerId);
            this.uiSettings = { ...this.uiSettings, 'ui.logo': null };
        },
        null,
        { throwError: true }
    );

    uploadFavicon = generateCreateEntity(
        'uploadFavicon',
        this,
        'uploadingFavicon',
        async (partnerId, formData) => {
            const result = await services.Partners.uploadFavicon(
                partnerId,
                formData
            );
            this.uiSettings = {
                ...this.uiSettings,
                'ui.favicon': result.url || result,
            };
            return result;
        },
        null,
        { throwError: true }
    );

    removeFavicon = generateDeleteEntity(
        'removeFavicon',
        this,
        'removingFavicon',
        async (partnerId) => {
            await services.Partners.removeFavicon(partnerId);
            this.uiSettings = { ...this.uiSettings, 'ui.favicon': null };
        },
        null,
        { throwError: true }
    );

    setCurrentPartnerId(id) {
        const numId = Number(id) || null;
        if (this.currentPartnerId !== numId) {
            this.currentPartnerId = numId;
            this.settingsLoaded = false;
        }
        if (numId) window.localStorage.setItem('partnerId', String(numId));
    }

    setPartnerName(partnerId, name) {
        const partners = this.authStore.currentUser?.superForAccounts;
        if (!partners) return;
        const partner = partners.find((p) => p.id === Number(partnerId));
        if (partner) partner.name = name;
    }

    async downloadCSV(partnerId) {
        if (this.isDownloading) return true;
        this.isDownloading = true;

        // setTimeout(async () => {
        try {
            const options = {
                responseType: 'blob',
            };

            let response = await services.Partners.companiesService(
                partnerId
            ).exportList(options);

            const url = window.URL.createObjectURL(
                new Blob([response.data], {
                    type: response.headers['content-type'],
                })
            );

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Companies.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } finally {
            this.isDownloading = false;
            this.selection.clearSelection();
        }
        //  }, 5000);
    }

    async update(companyId, values) {
        this.savingCompany = true;
        this.error = null;
        try {
            if (companyId) {
                let result = await services.Companies.update({
                    id: companyId,
                    ...values,
                });
                const { settings, ...rest } = result;

                Object.assign(this.editingCompany, rest);

                if (settings) {
                    this.editingCompany.uiSettings = settings;
                }
            } else {
                await services.Companies.super().create({ ...values }, true);
                await this.loadCompanies();
                return true;
            }

            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.savingCompany = false;
        }
    }

    clearEditingCompany() {
        this.editingCompany = null;
    }

    async loadCompany(companyId, partnerId) {
        if (this.loadingCompany) return;
        this.error = null;
        if (this.editingCompany != null) {
            if (this.editingCompany.id === companyId) return;
            else this.editingCompany = null;
        }
        try {
            this.loadingCompany = true;

            const result = partnerId
                ? await services.Partners.companiesService(partnerId).fetch(
                      companyId
                  )
                : await services.Companies.fetch(companyId);
            runInAction(() => {
                this.editingCompany = result;
            })
            return result;
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            })
        } finally {
            runInAction(() => {
                this.loadingCompany = false;
            })
        }
    }

    async addManager(companyId, data) {
        this.addingManager = true;
        this.error = null;
        try {
            await services.Companies.managersService(companyId).create(data);
            this.editingCompany = await services.Companies.fetch(companyId);
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.addingManager = false;
        }
    }

    async removeManager(companyId, email) {
        this.error = null;
        try {
            await services.Companies.managersService(companyId).delete(email);
            this.editingCompany = await services.Companies.fetch(companyId);
        } catch (e) {
            this.error = extractErrorMessage(e);
        }
    }

    async createPPV(companyId) {
        this.savingPPV = true;
        this.error = null;
        try {
            let ppv = await services.Companies.createPPV(companyId);
            this.editingCompany.ppv_package = ppv;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.savingPPV = false;
        }
    }

    async addViews(companyId, views, notes) {
        if (!views) return;
        this.savingPPV = true;
        this.error = null;
        try {
            let ppv = await services.Companies.addViews(companyId, {
                views,
                notes,
            });
            this.editingCompany.ppv_package = ppv;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.savingPPV = false;
        }
    }

    async loadPPVLog(companyId) {
        this.loadingPPVLog = true;
        this.error = null;
        try {
            let ppvLog = await services.Companies.ppvLog(companyId);
            this.ppvLog.replace(ppvLog);
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingPPVLog = false;
        }
    }

    get enabledLanguages() {
        return this.editingCompany ? this.editingCompany.languages.filter((lang) => lang.enabled).map((lang) => lang.code) : [];
    }

    get editingCompanyUISettings() {
        if (!this.editingCompany) return {};

        return (
            this.editingCompany.uiSettings || this.editingCompany.settings || {}
        );
    }


    async loadPPVTransactions(companyId) {
        this.loadingPPVTransactions = true;
        this.error = null;
        try {
            let ppvTransactions = await services.Companies.ppvTransactions(
                companyId
            );
            this.ppvTransactions.replace(ppvTransactions);
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingPPVTransactions = false;
        }
    }

    async newPPVApiKey(companyId) {
        this.savingPPVApiKey = true;
        this.error = null;
        try {
            let ppv = await services.Companies.newPPVApiKey(companyId);
            this.editingCompany.ppv_package = ppv;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.savingPPVApiKey = false;
        }
    }

    async uploadLogoAsPartnerAdmin(companyId, formData) {
        this.uploadingLogoAsPartner = true;
        try {
            let result = await services.Resources.addLogo(companyId, formData);
            this.editingCompany = await services.Companies.fetch(companyId);
            if (this.editingCompany) {
                Object.assign(this.editingCompany, result, {
                    logo_name: result.logo_name,
                });
            }
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.uploadingLogoAsPartner = false;
        }
    }

    async removeLogoAsPartnerAdmin(companyId) {
        try {
            let result = await services.Resources.removeLogo(companyId);
            this.editingCompany = await services.Companies.fetch(companyId);
            if (this.editingCompany) {
                Object.assign(this.editingCompany, result, { logo_name: null });
            }
            //await services.Resources.removeLogo(companyId);
        } catch (e) {
            this.error = extractErrorMessage(e);
        }
    }

    async loadCompanies() {
        this.loadingCompanies = true;
        this.error = null;
        this.companies.clear();
        try {
            const result = await services.Companies.list();
            this.companies.replace(result);
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingCompanies = false;
        }
    }

    async deleteCompany(partnerId, companyId) {
        this.removingCompanies.push(companyId);
        this.error = null;
        try {
            await services.Partners.companiesService(partnerId).delete(
                companyId
            );
            let existing = this.companies.find((x) => x.id === companyId);
            if (existing) this.companies.remove(existing);
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.removingCompanies.remove(companyId);
        }
    }

    async loadPartnerCompanies(partnerId) {
        this.loadingPartnerCompanies = true;
        this.error = null;
        this.companies.clear();
        try {
            const result = await services.Partners.companiesService(
                partnerId
            ).list();
            result.forEach(
                (c) => (c.link = `/partner/${partnerId}/companies/${c.id}`)
            );
            this.companies.replace(result);
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingPartnerCompanies = false;
        }
    }

    get filteredPartnerCompanies() {
        return this.companies
            .filter(
                (x) =>
                    !(
                        this.companySearchValue.length > 0 &&
                        x.name !== null &&
                        !x.name
                            .toLowerCase()
                            .includes(this.companySearchValue.toLowerCase())
                    )
            )
            .sort(
                (companyA, companyB) =>
                    new Date(companyB.created) - new Date(companyA.created)
            );
    }

    async loadPartnerSubscriptionsOverview(partnerId) {
        this.loadingPartnerSubscriptionsOverview = true;
        this.error = null;

        try {
            this.partnerSubscriptionsOverview =
                await services.Partners.subscriptionsService(
                    partnerId
                ).overview();
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingPartnerSubscriptionsOverview = false;
        }
    }

    async collectPartnerPayment(partnerId) {
        this.collectingPayment = true;
        this.error = null;
        try {
            await services.Partners.subscriptionsService(partnerId).collect();
            if (this.partnerSubscriptionsOverview)
                this.partnerSubscriptionsOverview.collected = true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.collectingPayment = false;
        }
    }

    async openManagePartnerSession(partnerId) {
        return services.Partners.openManageSession(partnerId);
    }

    isCompanyValid(company) {
        const validTo = company.valid_to;
        return validTo && new Date(validTo) > new Date();
    }

    isCompanyInvalid(company) {
        return !this.isCompanyValid(company);
    }

    get activeCompaniesCount() {
        return this.companies.filter((x) => this.isCompanyValid(x)).length;
    }

    get inactiveCompaniesCount() {
        return this.companies.filter((x) => this.isCompanyInvalid(x)).length;
    }
}

export default PartnerStore;
